/* eslint-disable eqeqeq */
import { useParams } from "react-router-dom";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useGetCategoriesLinkedToProduct } from "../../../../hooks/api/queries";
import { useSellModeFilters } from "../../../../hooks/useSellModeFilters";
import { truncateString } from "../../../../helpers";
import styles from "./styles.module.scss";
import { CategoryExclamation } from "../../../../assets/svgIcons";
import secondaryComponents from "../..";
import { AllCategoriesView } from "../AllCategoriesView";

export const SellModeCategory = () => {
  const { store_id } = useParams();
  const { data: categories, isLoading } = useGetCategoriesLinkedToProduct({
    queries: `?store_id=${store_id}`,
  });

  const { search, filterByCategory } = useSellModeFilters();
  const containerRef = useRef(null);

  const [visibleCategories, setVisibleCategories] = useState([]);
  const [showMore, setShowMore] = useState(false);
  const [isArrangeDone, setIsArrangeDone] = useState(false);

  // Function to handle category filtering
  const handleCategoryClick = useCallback(
    (categoryId) => {
      filterByCategory(categoryId);
    },
    [filterByCategory],
  );

  useEffect(() => {
    if (!categories || categories?.length === 0) return;

    const container = containerRef.current;
    if (!container) return;

    const categoryElements = Array.from(container.children);
    if (categoryElements?.length === 0) return;

    let totalWidth = 40;
    const containerWidth = container.clientWidth;
    let fitCategories = [];

    categoryElements.forEach((el, i) => {
      const elWidth = el.offsetWidth + 12;
      if (totalWidth + elWidth > containerWidth - 250) return;
      totalWidth += elWidth;
      fitCategories.push(`${categories[i]?.id}`);
    });

    // Ensure selected category is visible
    const selectedCategory = search.get("category_id");
    if (selectedCategory && !fitCategories.includes(selectedCategory)) {
      fitCategories[fitCategories.length - 1] = selectedCategory;
    }

    setIsArrangeDone(true);
    setVisibleCategories(fitCategories);
  }, [categories, search]);

  // Ensure selected category is visible in the list
  useEffect(() => {
    const selectedCategory = search.get("category_id");
    if (!selectedCategory) return;

    setVisibleCategories((prev) => {
      if (prev.includes(selectedCategory)) return prev;
      return [...prev.slice(0, -1), selectedCategory];
    });
  }, [search]);

  // Memoized filtered categories to improve performance
  const filteredCategories = useMemo(
    () =>
      categories?.filter(
        (category) =>
          visibleCategories.includes(`${category?.id}`) || !isArrangeDone,
      ) || [],
    [categories, visibleCategories, isArrangeDone],
  );

  return (
    <>
      <section
        ref={containerRef}
        aria-label="Category selection"
        className={styles.categories}
        aria-live="polite"
      >
        <secondaryComponents.LoaderHelper
          isLoading={isLoading}
          classNames="py-3 mx-auto"
        >
          <button
            type="button"
            className={`${styles.category} ${!search?.get("category_id") ? styles.active : ""} ${styles.all}`}
            onClick={() => handleCategoryClick(null)}
            aria-pressed={!search?.get("category_id")}
          >
            All
          </button>

          {filteredCategories?.map((category) => (
            <button
              key={category?.id}
              type="button"
              className={`${styles.category} ${search?.get("category_id") == category?.id ? styles.active : ""}`}
              onClick={() => handleCategoryClick(category?.id)}
              aria-pressed={search?.get("category_id") == category?.id}
            >
              {truncateString(category?.name, 15)}
            </button>
          ))}

          <button
            type="button"
            className={`${styles.category} ${styles.more}`}
            onClick={() => setShowMore(true)}
          >
            <span>View More</span>
            <CategoryExclamation />
          </button>
        </secondaryComponents.LoaderHelper>
      </section>

      <secondaryComponents.Modal
        isActive={showMore}
        closeModal={() => setShowMore(false)}
        width={1300}
        padding="15px 0 50px 0"
        aria-modal="true"
        role="dialog"
      >
        <AllCategoriesView closeModal={() => setShowMore(false)} />
      </secondaryComponents.Modal>
    </>
  );
};
